import FilterButton from "app/components/common/FilterButton";
import React, { useState, useMemo } from "react";
import CommonFilterDialog from "../CommonFilterDialog";
import {
  Box,
  Checkbox,
  DialogContent,
  FormControlLabel,
  Grid,
  MenuItem,
  CircularProgress,
  Divider,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import {
  CustomSelectBox,
  CustomTextField,
} from "assets/styles/globalStyledComponent";
import { FormatFullName, MenuProps } from "app/utils/helper";
import { Distrct, States } from "app/config/administrativeConstants";
import CustomChip from "app/components/common/CustomChip";
import { DatePicker } from "@mui/x-date-pickers";
import {
  getSchoolByName,
  getDistinctTags,
} from "app/services/org/school.service";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import {
  getDistinctActiveSessionDropdown,
  getEkalsutraStaff,
} from "app/services/org/crm/crm.service";
import StaffSearchCard from "app/components/Tables/StaffSearchCard";
import useSelectorHook from "app/services/redux/useSelectorHook";

// Constants
const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "ACTIVE" },
  { value: "INACTIVE", label: "INACTIVE" },
];

const SESSION_OPTIONS = [
  "2021-2022",
  "2022-2023",
  "2023-2024",
  "2024-2025",
  "2025-2026",
  "2026-2027",
  "2027-2028",
];

const FILTER_FIELDS = {
  DISTRICT: "district",
  CREATED_BY: "createdBy",
  CREATED_ON: "createdOn",
  STATUS: "status",
  SCHOOL_NAME: "schoolName",
  ACTIVE_SESSION: "activeSession",
  SESSION: "session",
  TAGS: "tags",
};

// Initial filter state
const getInitialFilterState = (selectedSession) => ({
  checkboxes: {
    [FILTER_FIELDS.DISTRICT]: false,
    [FILTER_FIELDS.CREATED_BY]: false,
    [FILTER_FIELDS.CREATED_ON]: false,
    [FILTER_FIELDS.STATUS]: false,
    [FILTER_FIELDS.SCHOOL_NAME]: false,
    [FILTER_FIELDS.ACTIVE_SESSION]: false,
    [FILTER_FIELDS.SESSION]: true,
    [FILTER_FIELDS.TAGS]: false,
  },
  state: "",
  district: "",
  createdBy: "",
  from: "",
  to: "",
  status: "",
  activeSession: "",
  session: selectedSession || "",
  selectedTags: [],
});

const InstituteListFilter = ({ mutate = () => {} }) => {
  const { selectedSession } = useSelectorHook();
  const [filterDialog, setFilterDialog] = useState(false);
  const [schoolSearch, setSchoolSearch] = useState({
    searchText: "",
    selectedSchools: [],
    page: 1,
    menuOpen: false,
    data: {},
  });
  const [filterState, setFilterState] = useState(() =>
    getInitialFilterState(selectedSession),
  );
  // ...existing code...
  const {
    isFetching: getSchoolByNameFetching,
    isLoading: getSchoolByNameLoading,
  } = useQuery({
    queryKey: ["getStudentByName", schoolSearch.searchText, schoolSearch.page],
    queryFn: () =>
      getSchoolByName({
        session: filterState.session,
        schoolName: schoolSearch.searchText,
        page: schoolSearch.page,
      }),
    onSuccess: (data) => {
      setSchoolSearch((prev) => ({
        ...prev,
        data: prev.page !== 1 ? { ...prev.data, ...data?.data } : data?.data,
      }));
    },
    onError: ({ response: { data } }) => {
      toast.error(data?.message);
    },
    enabled: Boolean(schoolSearch.searchText),
  });

  const { data: staffData } = useQuery({
    queryKey: ["getEkalsutraStaff"],
    queryFn: getEkalsutraStaff,
    onError: (err) => toast.error(err?.response?.data?.message),
  });

  const { data: activeSessionsData } = useQuery({
    queryKey: ["getDistinctActiveSessionDropdown"],
    queryFn: getDistinctActiveSessionDropdown,
    onError: (err) => toast.error(err?.response?.data?.message),
  });

  const { data: tagsData } = useQuery({
    queryKey: ["getDistinctTags"],
    queryFn: getDistinctTags,
    onError: (err) => toast.error(err?.response?.data?.message),
  });
  // Build filter params based on enabled checkboxes
  const buildFilterParams = () => {
    const params = {};
    const { checkboxes } = filterState;

    if (checkboxes.createdOn) {
      params.from = filterState.from;
      params.to = filterState.to;
    }

    if (checkboxes.district && filterState.district) {
      params.dist = filterState.district;
    }

    if (checkboxes.activeSession && filterState.activeSession) {
      params.activeSession = filterState.activeSession;
    }

    if (checkboxes.createdBy && filterState.createdBy?.staffDocId) {
      params.createdByPersonDocId = filterState.createdBy.staffDocId;
    }

    if (checkboxes.status && filterState.status) {
      params.accountStatus = filterState.status;
    }

    if (checkboxes.schoolName && schoolSearch.selectedSchools.length > 0) {
      params.reqSchoolDocIdList = JSON.stringify(
        schoolSearch.selectedSchools.map((school) => school.schoolDocId),
      );
    }

    if (checkboxes.session && filterState.session) {
      params.session = filterState.session;
    }

    if (checkboxes.tags && filterState.selectedTags.length > 0) {
      params.tags = JSON.stringify(filterState.selectedTags);
    }

    return params;
  };

  const handleFilter = () => {
    mutate(buildFilterParams());
    setFilterDialog(false);
  };
  const toggleCheckbox = (fieldName) => {
    setFilterState((prev) => ({
      ...prev,
      checkboxes: {
        ...prev.checkboxes,
        [fieldName]: !prev.checkboxes[fieldName],
      },
    }));
  };

  const updateFilterValue = (field, value) => {
    setFilterState((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSchoolSelection = (school) => {
    setSchoolSearch((prev) => {
      const isSelected = prev.selectedSchools.some(
        (s) => s.schoolDocId === school.schoolDocId,
      );
      return {
        ...prev,
        selectedSchools: isSelected
          ? prev.selectedSchools.filter(
              (s) => s.schoolDocId !== school.schoolDocId,
            )
          : [...prev.selectedSchools, school],
      };
    });
  };

  const removeSchoolFromSelection = (schoolDocId) => {
    setSchoolSearch((prev) => ({
      ...prev,
      selectedSchools: prev.selectedSchools.filter(
        (s) => s.schoolDocId !== schoolDocId,
      ),
    }));
  };

  // Count active filters
  const activeFilterCount = useMemo(
    () => Object.values(filterState.checkboxes).filter(Boolean).length,
    [filterState.checkboxes],
  );
  return (
    <>
      <FilterButton
        count={activeFilterCount}
        handleClick={() => setFilterDialog(true)}
      />
      <CommonFilterDialog
        handleSaveAndView={handleFilter}
        open={filterDialog}
        onClose={() => setFilterDialog(false)}
        minWidth="500px"
      >
        <DialogContent>
          {/* District Filter */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterState.checkboxes.district}
                  onChange={() => toggleCheckbox(FILTER_FIELDS.DISTRICT)}
                  color="secondary"
                />
              }
              label="District"
            />
            {filterState.checkboxes.district && (
              <Grid container spacing={1.5} sx={{ pt: 1.3 }}>
                <Grid item xs={12} md={6}>
                  <CustomTextField
                    select
                    value={filterState.state}
                    label="Select State"
                    onChange={(e) => updateFilterValue("state", e.target.value)}
                    fullWidth
                    size="small"
                    SelectProps={{ MenuProps }}
                  >
                    {States.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </CustomTextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomTextField
                    disabled={!filterState.state}
                    select
                    value={filterState.district}
                    onChange={(e) =>
                      updateFilterValue("district", e.target.value)
                    }
                    label="Select District"
                    fullWidth
                    size="small"
                    SelectProps={{ MenuProps }}
                  >
                    {Distrct[
                      States.indexOf(String(filterState.state)) + 1
                    ]?.map((district) => (
                      <MenuItem key={district} value={district}>
                        {district}
                      </MenuItem>
                    ))}
                  </CustomTextField>
                </Grid>
              </Grid>
            )}
          </Box>
          {/* Created By Filter */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterState.checkboxes.createdBy}
                  onChange={() => toggleCheckbox(FILTER_FIELDS.CREATED_BY)}
                  color="secondary"
                />
              }
              label="Created By"
            />
            {filterState.checkboxes.createdBy && (
              <Grid container spacing={1.5} sx={{ pt: 1.3 }}>
                <Grid item xs={12} md={6}>
                  <CustomSelectBox
                    displayEmpty
                    fullWidth
                    size="small"
                    MenuProps={MenuProps}
                    value={filterState.createdBy}
                    onChange={(e) =>
                      updateFilterValue("createdBy", e.target.value)
                    }
                    renderValue={(selected) => (
                      <Typography>
                        {selected
                          ? FormatFullName(
                              selected.firstName,
                              selected.middleName,
                              selected.lastName,
                            )
                          : "Select"}
                      </Typography>
                    )}
                  >
                    <MenuItem disabled value="">
                      Select
                    </MenuItem>
                    {staffData?.data?.list?.map((staff) => (
                      <MenuItem key={staff.staffDocId} value={staff}>
                        <StaffSearchCard item={staff} />
                      </MenuItem>
                    ))}
                  </CustomSelectBox>
                </Grid>
              </Grid>
            )}
          </Box>
          {/* Created On Filter */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterState.checkboxes.createdOn}
                  onChange={() => toggleCheckbox(FILTER_FIELDS.CREATED_ON)}
                  color="secondary"
                />
              }
              label="Created On"
            />
            {filterState.checkboxes.createdOn && (
              <Grid container spacing={1.5} sx={{ pt: 1.3 }}>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    inputFormat="DD-MM-YYYY"
                    value={filterState.from}
                    onChange={(date) => updateFilterValue("from", date)}
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        size="small"
                        fullWidth
                        label="From"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    inputFormat="DD-MM-YYYY"
                    value={filterState.to}
                    onChange={(date) => updateFilterValue("to", date)}
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        size="small"
                        fullWidth
                        label="To"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
          {/* Active Session Filter */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterState.checkboxes.activeSession}
                  onChange={() => toggleCheckbox(FILTER_FIELDS.ACTIVE_SESSION)}
                  color="secondary"
                />
              }
              label="Active Session"
            />
            {filterState.checkboxes.activeSession && (
              <Grid container sx={{ pt: 1.3 }}>
                <Grid item xs={12}>
                  <CustomSelectBox
                    fullWidth
                    displayEmpty
                    value={filterState.activeSession}
                    onChange={(e) =>
                      updateFilterValue("activeSession", e.target.value)
                    }
                    size="small"
                    MenuProps={MenuProps}
                  >
                    <MenuItem disabled value="">
                      <em>Select</em>
                    </MenuItem>
                    {activeSessionsData?.data?.list?.map((session, idx) => (
                      <MenuItem value={session} key={idx}>
                        {session}
                      </MenuItem>
                    ))}
                  </CustomSelectBox>
                </Grid>
              </Grid>
            )}
          </Box>
          {/* Status Filter */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterState.checkboxes.status}
                  onChange={() => toggleCheckbox(FILTER_FIELDS.STATUS)}
                  color="secondary"
                />
              }
              label="Status"
            />
            {filterState.checkboxes.status && (
              <Grid container spacing={2} sx={{ pt: 1.3 }}>
                <Grid item xs={12} md={5}>
                  <CustomSelectBox
                    fullWidth
                    displayEmpty
                    value={filterState.status}
                    size="small"
                    MenuProps={MenuProps}
                    onChange={(e) =>
                      updateFilterValue("status", e.target.value)
                    }
                  >
                    <MenuItem disabled value="">
                      <em>Select</em>
                    </MenuItem>
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </CustomSelectBox>
                </Grid>
              </Grid>
            )}
          </Box>
          {/* Session Filter */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterState.checkboxes.session}
                  onChange={() => toggleCheckbox(FILTER_FIELDS.SESSION)}
                  color="secondary"
                />
              }
              label="Session"
            />
            {filterState.checkboxes.session && (
              <Grid container spacing={2} sx={{ pt: 1.3 }}>
                <Grid item xs={12} md={5}>
                  <CustomSelectBox
                    fullWidth
                    displayEmpty
                    value={filterState.session}
                    size="small"
                    MenuProps={MenuProps}
                    onChange={(e) =>
                      updateFilterValue("session", e.target.value)
                    }
                  >
                    <MenuItem disabled value="">
                      <em>Select</em>
                    </MenuItem>
                    {SESSION_OPTIONS.map((session) => (
                      <MenuItem key={session} value={session}>
                        {session}
                      </MenuItem>
                    ))}
                  </CustomSelectBox>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Tags Filter */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterState.checkboxes.tags}
                  onChange={() => toggleCheckbox(FILTER_FIELDS.TAGS)}
                  color="secondary"
                />
              }
              label="Tags"
            />
            {filterState.checkboxes.tags && (
              <Box>
                <Grid container spacing={2} sx={{ pt: 1.3 }}>
                  <Grid item xs={12} md={5}>
                    <CustomSelectBox
                      fullWidth
                      displayEmpty
                      multiple
                      value={filterState.selectedTags}
                      size="small"
                      MenuProps={MenuProps}
                      onChange={(e) =>
                        updateFilterValue("selectedTags", e.target.value)
                      }
                      renderValue={(selected) => (
                        <Typography>
                          {selected.length > 0
                            ? `${selected.length} tag(s) selected`
                            : "Select Tags"}
                        </Typography>
                      )}
                    >
                      <MenuItem disabled value="">
                        <em>Select Tags</em>
                      </MenuItem>
                      {tagsData?.data?.list?.map((tag, idx) => (
                        <MenuItem key={idx} value={tag}>
                          <Checkbox
                            checked={filterState.selectedTags.indexOf(tag) > -1}
                            color="secondary"
                          />
                          <Typography>{tag}</Typography>
                        </MenuItem>
                      ))}
                    </CustomSelectBox>
                  </Grid>
                </Grid>
                <Stack direction="row" gap={1} flexWrap="wrap" pt={1}>
                  {filterState.selectedTags.map((tag, idx) => (
                    <CustomChip
                      key={idx}
                      deleteIcon
                      handleClose={() => {
                        const updatedTags = filterState.selectedTags.filter(
                          (t) => t !== tag,
                        );
                        updateFilterValue("selectedTags", updatedTags);
                      }}
                      title={tag}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>

          {/* School Name Filter */}
          <Stack>
            <FormControlLabel
              control={
                <Checkbox
                  color="secondary"
                  checked={filterState.checkboxes.schoolName}
                  onChange={() => toggleCheckbox(FILTER_FIELDS.SCHOOL_NAME)}
                />
              }
              label="By Name"
            />
            {filterState.checkboxes.schoolName && (
              <Box>
                <Grid container spacing={2} sx={{ pt: 1.3 }}>
                  <Grid item xs={12} md={5}>
                    <CustomSelectBox
                      size="small"
                      sx={{ width: "250px", backgroundColor: "#fff" }}
                      MenuProps={MenuProps}
                      multiple
                      value={schoolSearch.selectedSchools}
                      fullWidth
                      open={schoolSearch.menuOpen}
                      onOpen={() =>
                        setSchoolSearch((prev) => ({ ...prev, menuOpen: true }))
                      }
                      onClose={() =>
                        setSchoolSearch((prev) => ({
                          ...prev,
                          menuOpen: false,
                        }))
                      }
                      onChange={(e) => e.stopPropagation()}
                      renderValue={() => <Typography>Select</Typography>}
                    >
                      <Box sx={{ px: 1 }}>
                        <CustomTextField
                          autoFocus
                          sx={{ mb: 0.5 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {(getSchoolByNameFetching ||
                                  getSchoolByNameLoading) && (
                                  <CircularProgress size={20} />
                                )}
                              </InputAdornment>
                            ),
                          }}
                          placeholder="Type School Name"
                          onChange={(e) => {
                            e.stopPropagation();
                            setSchoolSearch((prev) => ({
                              ...prev,
                              searchText: e.target.value,
                              page: 1,
                              data: e.target.value ? prev.data : {},
                            }));
                          }}
                          value={schoolSearch.searchText}
                          fullWidth
                          size="small"
                        />
                        {schoolSearch.data?.list?.map((school) => (
                          <Box key={school.schoolDocId}>
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={10}>
                                <Typography
                                  sx={{ fontWeight: 600, fontSize: "14px" }}
                                >
                                  {school.schoolName}
                                </Typography>
                                <Typography
                                  sx={{ fontWeight: 500, fontSize: "12px" }}
                                >
                                  School Code: {school.schoolCode}
                                </Typography>
                                <Typography
                                  sx={{ fontWeight: 500, fontSize: "12px" }}
                                >
                                  License No: {school.ekalRefNumber}
                                </Typography>
                                <Typography
                                  sx={{ fontWeight: 500, fontSize: "12px" }}
                                >
                                  Account Status: {school.accountStatus}
                                </Typography>
                              </Grid>
                              <Grid item xs={2}>
                                <Checkbox
                                  color="secondary"
                                  checked={schoolSearch.selectedSchools.some(
                                    (s) => s.schoolDocId === school.schoolDocId,
                                  )}
                                  onChange={() => toggleSchoolSelection(school)}
                                />
                              </Grid>
                            </Grid>
                            <Divider
                              sx={{ my: 0.5, backgroundColor: "#3b3939" }}
                            />
                          </Box>
                        ))}
                        {schoolSearch.data?.pages !==
                          schoolSearch.data?.page && (
                          <Stack direction="row" justifyContent="center">
                            <Typography
                              sx={{
                                color: "blue",
                                textDecoration: "underline",
                                cursor: "pointer",
                                mb: 1,
                              }}
                              onClick={() =>
                                setSchoolSearch((prev) => ({
                                  ...prev,
                                  page: prev.page + 1,
                                }))
                              }
                            >
                              more
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                    </CustomSelectBox>
                  </Grid>
                </Grid>
                <Stack direction="row" gap={1} flexWrap="wrap" pt={1}>
                  {schoolSearch.selectedSchools.map((school) => (
                    <CustomChip
                      key={school.schoolDocId}
                      deleteIcon
                      handleClose={() =>
                        removeSchoolFromSelection(school.schoolDocId)
                      }
                      title={school.schoolName}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
      </CommonFilterDialog>
    </>
  );
};

export default InstituteListFilter;
